import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, Button, EmptyState, Icon, Modal, Skeleton, gradientOf } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import api from "../../services/api";
import LlmPanel, { useLlmState } from "../ai/LlmPanel";
import * as llm from "../../services/ai/engine";
import { getSelectedLlm } from "../../services/ai/models";
import { IS_MOCK } from "../../services/config";
import { characterPic } from "../../assets/characterImages";
import { errorInfo } from "../../services/errors";
import ChatInfo, { RelationBars } from "./ChatInfo";
import Composer from "./Composer";
import GiftFx from "./GiftFx";
import { BannedDialog, ConfirmDialog, GiftDialog, ModelDialog, NoteDialog, RenameDialog } from "./Dialogs";
import MessageItem, { TypingBubble } from "./MessageItem";
import PickDialog from "./PickDialog";
import { INTIMACY_LEVELS, ROMANTIC_LEVELS, dayLabel, levelLabel, sameDay } from "./format";

/** ห้องแชท: โหลดข้อมูลห้อง+ข้อความ (แบ่งหน้า) ครั้งเดียว, ส่งข้อความแบบ optimistic, เจนใหม่, ของขวัญ, ตั้งค่า */
export default function ChatRoom({ chatId, onChanged, onGone }) {
  const { profile, coins: ctxCoins, setCoins } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const coins = typeof profile?.tokensLeft === "number" ? ctxCoins : undefined;

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [models, setModels] = useState(null);
  const [msgs, setMsgs] = useState({ items: null, nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stream, setStream] = useState(""); // คำตอบที่กำลังไหลมาทีละคำ
  const [llmOpen, setLlmOpen] = useState(false);
  const [fx, setFx] = useState(null); // เอฟเฟกต์ของขวัญล่าสุด
  const [joy, setJoy] = useState({ seq: 0, msgId: null }); // ตัวละครดีใจ: อวาตาร์เด้ง + หัวใจ
  const [resending, setResending] = useState(false);
  const fxSeq = useRef(0);
  const llmState = useLlmState();
  const boxRef = useRef(null);
  const op = useRef("init"); // ชนิดการเปลี่ยนรายการข้อความ: init | append | prepend | keep
  const prevScroll = useRef(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.chats.detail(chatId), api.chats.messagesPage(chatId)])
      .then(([d, m]) => {
        if (!alive) return;
        op.current = "init";
        setInfo(d);
        setMsgs({ items: m.items, nextCursor: m.nextCursor });
      })
      .catch((e) => alive && setError(errorInfo(e).message));
    api.models.list().then((m) => alive && setModels(m)).catch(() => {});
    return () => { alive = false; };
  }, [chatId]);

  // ยังไม่ได้โหลด -> โหลดเลย (App เริ่มโหลดตั้งแต่เข้าเว็บแล้ว อันนี้กันกรณีหลุด)
  useEffect(() => {
    llm.autoLoad(getSelectedLlm());
  }, []);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el || msgs.items === null) return;
    if (op.current === "init") el.scrollTop = el.scrollHeight;
    else if (op.current === "prepend" && prevScroll.current) el.scrollTop = el.scrollHeight - prevScroll.current.h + prevScroll.current.t;
    else if (op.current === "append") el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    op.current = "keep";
  }, [msgs.items]);
  useEffect(() => {
    const el = boxRef.current;
    if ((sending || regenId) && el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [sending, regenId]);
  useEffect(() => {
    const el = boxRef.current;
    if (stream && el) el.scrollTop = el.scrollHeight;
  }, [stream]);

  const model = useMemo(() => (models || []).find((m) => m.id === info?.modelId) || info?.model || null, [models, info]);
  const cost = Number(model?.coinPerMessage) || 10;
  const modelId = info?.modelId || model?.id;
  const char = useMemo(() => ({ name: info?.character?.name || info?.name || "", seed: info?.characterId, colors: info?.colors, image: info?.image }), [info]);
  const displayName = profile?.displayName || "คุณ";

  const loadMore = useCallback(async () => {
    if (loadingMore || !msgs.nextCursor) return;
    setLoadingMore(true);
    try {
      const r = await api.chats.messagesPage(chatId, msgs.nextCursor);
      const el = boxRef.current;
      prevScroll.current = el ? { h: el.scrollHeight, t: el.scrollTop } : null;
      op.current = "prepend";
      setMsgs((s) => ({ items: [...r.items.filter((x) => !s.items.some((y) => y.id === x.id)), ...s.items], nextCursor: r.nextCursor }));
    } catch (e) {
      toast(errorInfo(e).message, "error");
    } finally {
      setLoadingMore(false);
    }
  }, [chatId, loadingMore, msgs.nextCursor, toast]);
  const onScroll = (e) => { if (e.currentTarget.scrollTop < 100) loadMore(); };

  /** ผลของข้อความล่าสุด -> อัปเดตค่าความสัมพันธ์/สตรีค/จำนวนข้อความทันที */
  const mergeChat = (c) => {
    if (!c) return;
    setInfo((i) => ({
      ...i,
      relationship: typeof c.romanticValue === "number"
        ? { ...i.relationship, romanticValue: c.romanticValue, romanticLabel: levelLabel(ROMANTIC_LEVELS, c.romanticValue), intimacyValue: c.intimacyValue, intimacyLabel: levelLabel(INTIMACY_LEVELS, c.intimacyValue) }
        : i.relationship,
      stats: { ...i.stats, messageCount: c.messageCount ?? i.stats.messageCount, streakCount: c.streakCount ?? i.stats.streakCount, streakBest: c.streakBest ?? i.stats.streakBest },
    }));
  };

  const send = async (override) => {
    const content = String(override ?? text).trim();
    if (!content || sending || regenId) return;
    // คำตอบมาจากไฟล์ที่เตรียมไว้ — ไม่ต้องรอโหลดโมเดล (โมเดลใช้แค่ปรับคำเมื่อพร้อมแล้ว)
    if (typeof coins === "number" && coins < cost) { setNotice({ needCoins: true, need: cost, have: coins, message: "คอยน์ไม่พอ" }); return; }
    setNotice(null);
    const tmpId = `tmp-${Date.now()}`;
    op.current = "append";
    setMsgs((s) => ({ ...s, items: [...s.items, { id: tmpId, sender: "user", type: "text", content, createdAt: new Date().toISOString(), pending: true }] }));
    setText("");
    setSending(true);
    try {
      const r = await api.chats.send(chatId, content, modelId, undefined, (_t, full) => setStream(full));
      op.current = "append";
      setMsgs((s) => ({ ...s, items: [...s.items.filter((x) => x.id !== tmpId), r.userMessage, r.aiMessage].filter(Boolean) }));
      if (typeof r.tokensLeft === "number") setCoins(r.tokensLeft);
      mergeChat(r.chat);
      onChanged?.({ id: chatId, lastPreview: String(r.aiMessage?.content || "").slice(0, 60), latestMessageTime: r.aiMessage?.createdAt || new Date().toISOString() });
    } catch (e) {
      const err = errorInfo(e);
      op.current = "keep";
      setMsgs((s) => ({ ...s, items: s.items.filter((x) => x.id !== tmpId) }));
      setText((t) => t || content); // คืนข้อความให้ผู้ใช้ ไม่ต้องพิมพ์ใหม่
      setNotice({ ...err, need: err.need ?? cost, retryText: content });
    } finally {
      setSending(false);
      setStream("");
    }
  };

  const items = msgs.items || [];
  const last = items[items.length - 1];
  const canRegenId = last && last.sender === "character" && last.type !== "gift" && items.length > 1 && items[items.length - 2].sender === "user" && items[items.length - 2].type !== "gift" ? last.id : null;
  const regen = async () => {
    if (!canRegenId || sending || regenId) return;
    if (typeof coins === "number" && coins < cost) { setNotice({ needCoins: true, need: cost, have: coins, message: "คอยน์ไม่พอ" }); return; }
    setNotice(null);
    setRegenId(canRegenId);
    try {
      const r = await api.chats.regenerate(chatId, modelId, (_t, full) => setStream(full));
      op.current = "keep";
      setMsgs((s) => ({ ...s, items: s.items.map((x) => (x.id === canRegenId ? r.aiMessage : x)) }));
      if (typeof r.tokensLeft === "number") setCoins(r.tokensLeft);
      onChanged?.({ id: chatId, lastPreview: String(r.aiMessage?.content || "").slice(0, 60) });
    } catch (e) {
      setNotice({ ...errorInfo(e), need: cost });
    } finally {
      setRegenId(null);
      setStream("");
    }
  };

  const like = async (m) => {
    const next = m.reaction === "like" ? null : "like";
    const set = (reaction) => setMsgs((s) => ({ ...s, items: s.items.map((x) => (x.id === m.id ? { ...x, reaction } : x)) }));
    op.current = "keep";
    set(next);
    try { await api.chats.react(chatId, m.id, next); } catch (e) { set(m.reaction || null); toast(errorInfo(e).message, "error"); }
  };
  const copy = (m) => {
    navigator.clipboard?.writeText(String(m.content || "").replace(/\*/g, "")).then(() => toast("คัดลอกข้อความแล้ว", "success"), () => toast("คัดลอกไม่สำเร็จ", "error"));
  };

  // ---- ตั้งค่าห้อง ----------------------------------------------------------
  const update = async (patch, local) => {
    await api.chats.update(chatId, patch);
    setInfo((i) => ({ ...i, ...local }));
  };
  const attempt = async (fn) => {
    try { await fn(); } catch (e) { toast(errorInfo(e).message, "error"); }
  };
  const toggle = (key, val) => attempt(async () => {
    if (key === "relationshipMode") await update({ relationshipMode: val }, { relationshipMode: val });
    else {
      const settings = { ...info.settings, langGuard: val };
      await update({ settings }, { settings });
    }
  });
  const pickModel = (m) => attempt(async () => {
    if (m.id !== modelId) await update({ modelId: m.id }, { modelId: m.id, model: m });
    setDialog(null);
    setNotice((n) => (n?.needCoins ? null : n));
  });
  const newChat = () => attempt(async () => {
    const c = await api.chats.create(info.characterId);
    navigate(`/chat/${c.id}`);
  });
  const share = () => {
    const url = `${window.location.origin}/c/${info.characterId}`;
    navigator.clipboard?.writeText(url).then(() => toast("คัดลอกลิงก์ตัวละครแล้ว", "success"), () => toast(url, "info"));
  };
  const confirmChat = async (action) => {
    setBusy(true);
    try {
      if (action === "delete") await api.chats.remove(chatId);
      else await api.chats.update(chatId, { isArchived: !info.isArchived });
      toast(action === "delete" ? "ลบแชทแล้ว" : info.isArchived ? "ย้ายกลับแล้ว" : "จัดเก็บแชทแล้ว", "success");
      setDialog(null);
      onChanged?.({ id: chatId, remove: true });
      onGone?.();
    } catch (e) {
      toast(errorInfo(e).message, "error");
    } finally {
      setBusy(false);
    }
  };
  const giftSent = (r, gift) => {
    op.current = "append";
    setMsgs((s) => ({ ...s, items: [...s.items, ...r.messages.filter((m) => !s.items.some((y) => y.id === m.id))] }));
    if (typeof r.tokensLeft === "number") setCoins(r.tokensLeft);
    mergeChat(r.chat);
    // ไม่เด้ง toast: แบนเนอร์/บับเบิลของขวัญ (แสดงราคา) + ยอดคอยน์ที่หัวเว็บ บอกผลอยู่แล้ว และ toast จะบังปุ่มบนมือถือ
    const ai = r.messages[r.messages.length - 1];
    if (ai) onChanged?.({ id: chatId, lastPreview: String(ai.content || "").slice(0, 60), latestMessageTime: ai.createdAt });
    // เอฟเฟกต์ + ตัวละครดีใจ (ระดับ/คอมโบมาจากผลของ server)
    const g = r.gift || {};
    fxSeq.current += 1;
    setFx({ seq: fxSeq.current, tier: g.tier || "small", icon: g.icon || gift.emoji || "🎁", name: g.name || gift.name, combo: g.combo || 1, cost: g.coinCost ?? gift.coinCost, giftId: gift.id, sender: displayName, charName: char.name });
    setJoy({ seq: fxSeq.current, msgId: ai && ai.sender === "character" ? ai.id : null });
  };
  const resendGift = async (ev) => {
    if (resending) return;
    if (typeof coins === "number" && coins < ev.cost) { setNotice({ needCoins: true, need: ev.cost, have: coins, message: "คอยน์ไม่พอ" }); return; }
    setResending(true);
    try {
      const r = await api.gifts.send(chatId, ev.giftId);
      giftSent(r, { id: ev.giftId, name: ev.name, emoji: ev.icon, coinCost: ev.cost });
    } catch (e) {
      toast(errorInfo(e).message, "error");
    } finally {
      setResending(false);
    }
  };

  if (error) {
    return (
      <section className="chat-room" aria-label="ห้องแชท">
        <EmptyState icon="😶" title="เปิดห้องแชทไม่ได้" action={<Link to="/chat" className="btn btn--primary">กลับไปรายการแชท</Link>}>{error}</EmptyState>
      </section>
    );
  }

  const open = (d) => { setSheet(false); setDialog(d); };
  const infoEl = info ? <ChatInfo info={info} model={model} onOpen={open} onToggle={toggle} onNewChat={newChat} onShare={share} /> : null;
  const bgStyle = info ? { backgroundImage: `url("${characterPic({ image: info.image, id: info.characterId || info.name }, "med")}")` } : { background: gradientOf(info?.colors, info?.name) };

  return (
    <>
      <section className="chat-room" aria-label="ห้องแชท">
        <div className="chat-room__bg" style={bgStyle} aria-hidden="true" />
        <header className="chat-room__head">
          <Button size="sm" icon variant="ghost" className="hide-desktop" aria-label="กลับไปรายการแชท" onClick={() => navigate("/chat")}><Icon name="back" /></Button>
          {info ? (
            <>
              <span className={`joy-wrap${joy.seq ? " is-joy" : ""}`} key={`joy${joy.seq}`}><Avatar name={info.name} seed={info.characterId} colors={info.colors} src={info.image} size={42} character /></span>
              <div className="grow"><div className="strong clamp-1">{info.name}</div><div className="faint clamp-1" style={{ fontSize: 12 }}>{String(info.character?.tagline || "").replaceAll("{{user}}", displayName)}</div></div>
              <Button size="sm" variant="ghost" className="only-narrow" onClick={() => setSheet(true)} aria-label="ข้อมูลแชทและตั้งค่า"><Icon name="settings" size={18} />ข้อมูล</Button>
            </>
          ) : <Skeleton height={42} width={220} />}
        </header>
        {info ? (
          <>
            <div className="chat-ctx">
              <button className="ctx-chip" onClick={() => setDialog("persona")}><span className="faint">บทบาท:</span> {info.persona?.name || "ยังไม่ได้เลือก"}</button>
              <button className="ctx-chip" onClick={() => setDialog("scenario")}><span className="faint">สถานการณ์:</span> {info.scenario?.name || "ยังไม่ได้เลือก"}</button>
              <span className="streak-pill" title="คุยต่อเนื่องรายวัน"><Icon name="sparkle" size={13} />{info.stats?.streakCount || 0} วัน</span>
            </div>
            <div className="chat-rel-strip only-narrow"><span className="rel-label"><Icon name="heart" size={14} filled /> {info.relationship?.romanticLabel || "เฉยๆ"}</span><RelationBars rel={info.relationship || {}} compact /></div>
          </>
        ) : null}

        {info && !IS_MOCK ? (
          <div className="llm-strip" role="status" aria-live="polite" style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 12px", fontSize: 12 }}>
            <span className="grow faint">
              {!llm.isSupported() ? "เบราว์เซอร์นี้ไม่รองรับ AI ในเครื่อง (ต้องใช้ WebGPU) — ยังคุยได้ตามปกติ"
                : llmState.status === "loading" ? `กำลังโหลดโมเดล ${Math.round((llmState.progress || 0) * 100)}%`
                : llmState.status === "ready" ? "AI พร้อม — ช่วยปรับคำตอบให้หลากหลายขึ้น"
                : "ยังไม่ได้โหลดโมเดล AI — คุยได้ตามปกติ (โหลดเพื่อให้ปรับคำตอบหลากหลายขึ้น)"}
            </span>
            {sending || regenId ? <Button size="sm" onClick={() => llm.abort()}>หยุดตอบ</Button>
              : llmState.status !== "ready" ? <Button size="sm" onClick={() => setLlmOpen(true)}>{llmState.status === "loading" ? "ดูความคืบหน้า" : "โหลดโมเดล"}</Button> : null}
          </div>
        ) : null}

        <div className="chat-room__msgs" ref={boxRef} onScroll={onScroll} role="log" aria-live="polite" aria-busy={msgs.items === null}>
          {msgs.items === null ? (
            <div className="stack" style={{ padding: "8px 0" }}><Skeleton height={64} width="70%" /><Skeleton height={44} width="45%" style={{ alignSelf: "flex-end" }} /><Skeleton height={90} width="75%" /></div>
          ) : (
            <>
              {msgs.nextCursor ? <button className="load-older" onClick={loadMore} disabled={loadingMore}>{loadingMore ? "กำลังโหลด..." : "โหลดข้อความเก่ากว่านี้"}</button> : null}
              {items.map((m, i) => (
                <React.Fragment key={m.id}>
                  {i === 0 || !sameDay(items[i - 1].createdAt, m.createdAt) ? <div className="msg-day">{dayLabel(m.createdAt)}</div> : null}
                  {regenId === m.id ? (stream ? <MessageItem m={{ id: "stream", sender: "character", type: "text", content: stream, streaming: true }} char={char} userName={displayName} /> : <TypingBubble char={char} />) : (
                    <MessageItem m={m} char={char} userName={displayName} personaName={info?.persona?.name} canRegen={canRegenId === m.id} regenCost={cost} joy={joy.msgId === m.id}
                      busy={sending || !!regenId} onRegen={regen} onLike={() => like(m)} onCopy={() => copy(m)} />
                  )}
                </React.Fragment>
              ))}
              {sending ? (stream ? <MessageItem m={{ id: "stream", sender: "character", type: "text", content: stream, streaming: true }} char={char} userName={displayName} /> : <TypingBubble char={char} />) : null}
            </>
          )}
        </div>

        <GiftFx event={fx} onResend={resendGift} resending={resending} />

        <Composer text={text} setText={setText} onSend={send} sending={sending || !!regenId} model={model} cost={cost} coins={coins}
          onModel={() => setDialog("model")} onGift={() => setDialog("gift")} notice={notice} onNotice={setNotice} disabled={!info} />
      </section>
      <aside className="chat-side" aria-label="ข้อมูลแชท">{infoEl || <Skeleton height={300} />}</aside>

      <Modal open={llmOpen} title="โมเดล AI ในเบราว์เซอร์" onClose={() => setLlmOpen(false)}><LlmPanel /></Modal>
      <Modal open={sheet} title="ข้อมูลแชท" onClose={() => setSheet(false)}>{infoEl}</Modal>

      {info ? (
        <>
          <ModelDialog open={dialog === "model"} models={models} value={modelId} coins={coins} onClose={() => setDialog(null)} onPick={pickModel} />
          <GiftDialog open={dialog === "gift"} chatId={chatId} charName={char.name} coins={coins} onClose={() => setDialog(null)} onSent={giftSent} />
          <PickDialog open={dialog === "persona"} kind="persona" title="เลือกบทบาทของคุณ" hint="บทบาทคือตัวตนของคุณในเรื่อง ตัวละครจะเรียกและปฏิบัติต่อคุณตามนี้" value={info.persona?.id}
            load={(scope) => api.personas.list(scope)} save={api.personas.save} onClose={() => setDialog(null)}
            onPick={async (p) => update({ personaId: p ? p.id : "" }, { persona: p || null })} />
          <PickDialog open={dialog === "scenario"} kind="scenario" title="เลือกสถานการณ์" hint="ฉากที่ใช้เป็นจุดเริ่มต้นของเรื่องในแชทนี้" value={info.scenario?.id} characterId={info.characterId}
            load={(scope) => api.scenarios.list(scope, info.characterId)} save={api.scenarios.save} onClose={() => setDialog(null)}
            onPick={async (s) => update({ scenarioId: s ? s.id : "" }, { scenario: s || null })} />
          <NoteDialog open={dialog === "note"} chatId={chatId} memory={info.memory} limit={info.memoryLimit} onClose={() => setDialog(null)}
            onSaved={(memory, limit) => { setInfo((i) => ({ ...i, memory, memoryLimit: limit ?? i.memoryLimit })); toast("บันทึกโน้ตแล้ว", "success"); }} />
          <BannedDialog open={dialog === "banned"} words={info.settings?.bannedWords} onClose={() => setDialog(null)}
            onSave={async (list) => { const settings = { ...info.settings, bannedWords: list }; await update({ settings }, { settings }); toast("บันทึกคำที่ไม่ต้องการแล้ว", "success"); }} />
          <RenameDialog open={dialog === "rename"} name={info.name} onClose={() => setDialog(null)}
            onSave={async (name) => { await update({ name }, { name }); onChanged?.({ id: chatId, name }); toast("เปลี่ยนชื่อแล้ว", "success"); }} />
          <ConfirmDialog open={dialog === "archive"} title={info.isArchived ? "ย้ายแชทกลับ?" : "จัดเก็บแชทนี้?"} confirmLabel={info.isArchived ? "ย้ายกลับ" : "จัดเก็บ"} busy={busy} onClose={() => setDialog(null)} onConfirm={() => confirmChat("archive")}>
            <p>แชทจะ{info.isArchived ? "กลับมาอยู่ในรายการปกติ" : "ถูกย้ายไปแท็บ \"จัดเก็บแล้ว\" และเปิดดูได้ภายหลัง"}</p>
          </ConfirmDialog>
          <ConfirmDialog open={dialog === "delete"} title="ลบแชทนี้?" confirmLabel="ลบแชท" danger busy={busy} onClose={() => setDialog(null)} onConfirm={() => confirmChat("delete")}>
            <p>แชทกับ <b>{info.name}</b> จะหายจากรายการของคุณ ย้อนกลับไม่ได้</p>
          </ConfirmDialog>
        </>
      ) : null}
    </>
  );
}
